import React from 'react';
import styled from 'styled-components';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { CeaModelResult } from '/server/integrations/ceaPricingModel';
import { getProjectQuoteForJwt } from '/imports/api/projectQuotes';
import { useMethod } from '/imports/hooks';
import { formatNumber, formatPhone } from '/imports/utils';
import { Project } from '/imports/api/projects';
import { DateTime } from 'luxon';
import { fileHref } from '/imports/api/files';

export const CeaDealerIndicativeEssProposalPageStyled = styled.div`
  @media screen, print {
    width: 1920px;
    position: absolute;
    top: 0;
    left: 0;
    .slide {
      width: 1920px;
      height: 1080px;
      border: 1px solid black;
      box-sizing: border-box;
      overflow: hidden;
      position: relative;
      color: '#58616a';
      h1, h2, h3, h4, img, p, div {
        padding: 0;
        margin: 0;
        position: absolute;
      }
      ul {
        margin: 0;
      }
    }
    .hide {
      display: none;
    }
  }
`;

/**
 * Renders the CEA dealer indicative ESS proposal slides from a JWT-authorized project quote.
 */
export const CeaDealerIndicativeEssProposalPage = () => {
  const params = new URLSearchParams(location.search);
  const jwt = params.get('jwt');
  const quoteId = params.get('quoteId');
  const [quote, loading] = useMethod(getProjectQuoteForJwt, { quoteId, jwt });
  if (loading) return <LoadingFullScreen />;
  if (!quote) return null;
  const results = quote.modelResult as CeaModelResult;
  const project = quote._project as Required<Project>;
  const address = project.address;
  return (
    <CeaDealerIndicativeEssProposalPageStyled className="cea-proposal">
      <div className="slide" style={{ backgroundImage: 'url("/images/cea/indicativeDealerEssPage1.png")', backgroundSize: 'cover', position: 'relative' }}>
        <img src={quote.presenter?.logo} alt="" style={{ position: 'absolute', top: '50px', right: '50px', height: '150px' }} />
        <div style={{ top: '520px', right: '50px', color: 'white', fontSize: '26px', textAlign: 'right', lineHeight: '1.2', width: '1000px', height: '400px' }}>
          <h2 style={{ position: 'relative', fontWeight: 'bold' }}>
            Clean Energy Alliance Solar Plus Business Program
          </h2>
          <h2 style={{ position: 'relative' }}>
            Energy Storage Proposal for
          </h2>
          <h2 style={{ position: 'relative', top: '40px' }}>
            {quote.name}<br />
            {DateTime.now().toFormat('MMMM d, yyyy')}
          </h2>
        </div>
      </div>
      <div className="slide" style={{ backgroundImage: 'url("/images/cea/indicativeDealerEssPage2.png")', backgroundSize: 'cover', position: 'relative' }}>
        <div style={{ top: '0', right: '0', width: '220px', height: '85px', background: '#078ca6' }} />
        <div style={{ top: '0', right: '0', width: '136px', height: '108px', background: '#078ca6' }} />
        <div style={{ position: 'absolute', top: '20px', right: '20px', height: '110px', width: '110px', backgroundImage: `url(${quote.presenter?.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <img src="/images/cea/ceaWhite.png" alt="CEA" style={{ position: 'absolute', bottom: '0px', left: '20px', width: '100px' }} />
        <div style={{ top: '200px', right: '50px', width: '560px', height: '450px', background: 'linear-gradient(to right, #068ca6, #359f76)', borderRadius: '25px' }}>
          <div style={{ position: 'relative', top: '5px', left: '5px', width: '550px', height: '440px', borderRadius: '20px', backgroundImage: `url('${fileHref(project.image)}')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        </div>
        <div style={{ top: '665px', right: '50px', fontSize: '26px', color: '#306796', width: '560px', textAlign: 'center' }}>{address?.line}, {address?.city}, {address?.state}</div>
        <div style={{ left: '115px', bottom: '200px', width: '1594px', fontWeight: 'bold', color: 'white', border: '1px solid transparent', boxSizing: 'border-box' }}>
          <span style={{ width: '370px', height: '50px', textAlign: 'center', display: 'inline-block', border: '1px solid transparent', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '32px' }}>${formatNumber(results.schedule[0][5])}</span>
          </span>
          <span style={{ marginLeft: '37px', width: '370px', height: '50px', textAlign: 'center', display: 'inline-block', border: '1px solid transparent', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '32px' }}>${formatNumber(results.schedule[10][5])}</span>
          </span>
          <span style={{ marginLeft: '37px', width: '370px', height: '50px', textAlign: 'center', display: 'inline-block', border: '1px solid transparent', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '32px' }}>${formatNumber(results.schedule[0][6])}</span>
          </span>
          <span style={{ marginLeft: '37px', width: '370px', height: '50px', textAlign: 'center', display: 'inline-block', border: '1px solid transparent', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '32px' }}>${formatNumber(results.schedule[10][6])}</span>
          </span>
        </div>
      </div>
      <div className="slide" style={{ backgroundImage: 'url("/images/cea/indicativeDealerEssPage3.png")', backgroundSize: 'cover', position: 'relative' }}>
        <div style={{ bottom: '0', right: '0', width: '170px', height: '150px', background: '#078ca6' }} />
        <div style={{ position: 'absolute', bottom: '20px', right: '20px', height: '140px', width: '140px', backgroundImage: `url(${quote.presenter?.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      </div>
      <div className="slide" style={{ backgroundImage: 'url("/images/cea/indicativeDealerEssPage4.png")', backgroundSize: 'cover', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '636px', left: '306px', width: '183px', height: '183px', borderRadius: '50%', backgroundImage: `url(${quote.presenter?.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div style={{ position: 'absolute', top: '836px', left: '106px', width: '600px', textAlign: 'center', fontSize: '32px', fontWeight: 'bold', color: '#132f4c' }}>{quote.presenter?.name}</div>
        <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', top: '667px', left: '730px', minWidth: '250px', height: '178px', textAlign: 'center', fontSize: '26px', fontWeight: 'bold', color: 'white' }}>
          <div style={{ position: 'relative', display: 'inline-block', padding: '0 16px', width: 'auto', flex: '0', lineHeight: '45px', background: '#2c9e54', borderRadius: '0 20px 20px 0' }}>{quote.presenter?.email}</div>
          <div style={{ position: 'relative', display: 'inline-block', padding: '0 16px', width: 'auto', flex: '0', lineHeight: '45px', background: '#2c9e54', borderRadius: '0 20px 20px 0' }}>{formatPhone(quote.presenter?.phone)}</div>
          <div style={{ position: 'relative', display: 'inline-block', padding: '0 16px', width: 'auto', flex: '0', lineHeight: '45px', background: '#2c9e54', borderRadius: '0 20px 20px 0' }}>{quote.presenter?.website}</div>
        </div>
      </div>
    </CeaDealerIndicativeEssProposalPageStyled>
  );
};
